import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";

import { Box } from "@mui/material";

import { Layout } from "Layout";

import { NEW_RECORD_SKELETON } from "appConfig";

import { EditModalHeader } from "components/EditModalHeader";
import { FrmFieldGrid } from "components/FrmFieldGrid";
import { ObjectEditDialogContent } from "components/ObjectEditDialogContent";
import { FrmButtonGroupWithHoverHelp } from "components/formFields/FrmButtonGroupWithHoverHelp";
import { FrmTextField } from "components/formFields/FrmTextField";
import { FrmCheckboxWithHoverHelp } from "components/formFields/frm/FrmCheckboxWithHoverHelp";
import { FixedLoadingIndicator } from "components/utility/FixedLoadingIndicator";
import { FormErrors } from "components/utility/FormErrors";
import { Loading } from "components/utility/Loading";
import { VSpace } from "components/utility/VSpace";

import { i18n } from "services/i18nService";
import {
  INTERVAL_OPTIONS,
  REPEAT_OPTIONS,
} from "services/sosInventoryService/alert/schema";
import { getRecord } from "services/sosInventoryService/sosApi";

import { useErrors } from "hooks/useErrors";
import { useRecord } from "hooks/useRecord";

import { AlertMethod } from "views/Alerts/Alert/AlertMethod";
import { AlertType } from "views/Alerts/Alert/AlertType";

import {
  editModalLoadingIndicatorOn,
  editModalLoadingIndicatorOff,
} from "globalState/loadingSlice";

export function Alert(props) {
  const { id, onClose, objectType } = props;

  const dispatch = useDispatch();

  const userId = useSelector(
    (state) => state.userCompanySettings.company.userId
  );

  const [record, setRecord, handleInputFieldChange] = useRecord();
  const { errors, setErrors, isInError } = useErrors();

  function handleScalarChange(fieldName, value) {
    handleInputFieldChange(fieldName, value.id);
  }

  const loadingState = useSelector((state) => state.loading.editModal);

  useEffect(() => {
    async function _getRecord() {
      if (id) {
        dispatch(editModalLoadingIndicatorOn());
        const response = await getRecord(objectType, id);
        setRecord(response);
        dispatch(editModalLoadingIndicatorOff());
      } else {
        const emptyRecord = NEW_RECORD_SKELETON[objectType](userId);
        setRecord(emptyRecord);
      }
    }
    _getRecord();
  }, [setRecord, id, objectType, dispatch, userId]);

  return (
    <>
      <EditModalHeader
        record={record}
        setRecord={setRecord}
        setReloadRecord={setRecord}
        objectType={objectType}
        setErrors={setErrors}
        text={record?.name || ""}
        handleClose={onClose}
      />

      {loadingState && <Loading />}

      {record && (
        <ObjectEditDialogContent>
          <Layout
            pageTitle={
              id
                ? `${i18n(`objectType.${objectType}.Sentence`)} ${
                    record?.name || ""
                  }`
                : i18n(`objectType.${objectType}.New`)
            }
          >
            <Box flexGrow="1" position="relative" m={2} mb={0}>
              <FormErrors errors={errors} setErrors={setErrors} />
              {record ? (
                <Box>
                  <FrmFieldGrid gridAutoFlow="dense">
                    <div style={{ gridColumn: "1 / 2" }}>
                      <FrmTextField
                        name="name"
                        label={i18n("frmLabel.Name")}
                        onValueBlur={handleInputFieldChange}
                        value={record.name}
                        error={isInError("name")}
                      />
                      <VSpace space={1} />
                      <FrmCheckboxWithHoverHelp
                        name="active"
                        value={record.active}
                        onValueChange={handleInputFieldChange}
                        label={i18n("frmLabel.Enabled")}
                        helpText={i18n("hoverHelp.alert.ActiveAlert")}
                      />
                      <VSpace space={1} />
                      <FrmButtonGroupWithHoverHelp
                        buttonProps={{
                          name: "repeat",
                          label: i18n("frmLabel.RepeatAlert"),
                          value: record.repeat ? { id: record.repeat } : null,
                          onValueChange: handleScalarChange,
                          options: REPEAT_OPTIONS,
                          disabled: !record.active,
                          disableClearable: true,
                          sx: { flexGrow: 1 },
                        }}
                        helpText={i18n("hoverHelp.alert.RepeatAlert")}
                      />
                      <VSpace space={1} />
                      <FrmButtonGroupWithHoverHelp
                        buttonProps={{
                          name: "interval",
                          label: i18n("frmLabel.Interval"),
                          value: record.interval
                            ? { id: record.interval }
                            : null,
                          onValueChange: handleScalarChange,
                          options: INTERVAL_OPTIONS,
                          disabled: !record.active,
                          disableClearable: true,
                          sx: { flexGrow: 1 },
                        }}
                        helpText={i18n("hoverHelp.alert.AlertInterval")}
                      />
                      <VSpace space={1} />
                      <AlertMethod
                        user={record.user}
                        email={record.email}
                        method={record.method}
                        mobileNumber={record.mobileNumber}
                        mobileCarrier={record.mobileCarrier}
                        onChange={handleInputFieldChange}
                        onScalarChange={handleScalarChange}
                        disabled={!record.active}
                        isInError={isInError}
                      />
                    </div>
                    <div style={{ gridColumn: "2 / 2" }}>
                      <AlertType
                        record={record}
                        onValueChange={handleInputFieldChange}
                        handleScalarChange={handleScalarChange}
                        isInError={isInError}
                        disabled={!record.active}
                      />
                    </div>
                  </FrmFieldGrid>
                  <VSpace space={1} />
                  <FrmFieldGrid gridAutoFlow="dense"></FrmFieldGrid>
                </Box>
              ) : (
                !isInError("record") && (
                  <FixedLoadingIndicator
                    text={`${i18n("global.Loading")}...`}
                  />
                )
              )}
            </Box>
            <VSpace space={4} />
          </Layout>
        </ObjectEditDialogContent>
      )}
    </>
  );
}
